import { useReducer, useMemo, useEffect } from 'react';
import { calculateMonthlyCashflow, calcBruttoRendite, calcEKRendite, calcFaktor, calcBreakEvenZins, calculateKaufnebenkosten } from '../utils/dealCalc';

/**
 * STATE stores DISPLAY values (percentages as numbers: 20 = 20%, 3.5 = 3.5%).
 * Conversion to decimal (0.20, 0.035) happens ONLY when calling calc functions.
 */

/**
 * Deal Score from KPI-Referenz §2.
 * dealDelta = (purchasePricePerSqm / marketPricePerSqm) - 1
 * Max: 40 (Scoring V3)
 */
function calcDealScore(simKaufpreis, wohnflaeche, avgPricePerSqm) {
  if (!avgPricePerSqm || !wohnflaeche || avgPricePerSqm <= 0) return 12; // fallback: neutral
  const simPricePerSqm = simKaufpreis / wohnflaeche;
  const dealDelta = (simPricePerSqm / avgPricePerSqm) - 1;
  if (dealDelta <= -0.25) return 40;
  if (dealDelta <= -0.15) return 34;
  if (dealDelta <= -0.05) return 28;
  if (dealDelta <= 0)     return 22;
  if (dealDelta <= 0.10)  return 12;
  if (dealDelta <= 0.20)  return 5;
  return 0;
}

/**
 * Income Score from KPI-Referenz §3 (financing-independent, based on gross yield).
 * Max: 30 (Scoring V3)
 */
function calcIncomeScore(bruttoRendite) {
  if (bruttoRendite == null) return 12; // fallback: neutral
  if (bruttoRendite >= 6) return 30;
  if (bruttoRendite >= 5) return 26;
  if (bruttoRendite >= 4) return 21;
  if (bruttoRendite >= 3) return 16;
  if (bruttoRendite >= 2) return 10;
  return 5;
}

function buildInitial(baseline) {
  if (!baseline) return null;
  return {
    kaufpreisAnpassung: 0,    // % delta from baseline (-30 to +10)
    mieteAnpassung: 0,        // % delta from baseline (-30 to +50)
    leerstand: 0,             // Leerstandsmonate pro Jahr (0–6)
    eigenkapital: Math.round(baseline.eigenkapitalAnteil * 100),  // 20
    zinssatz: Math.round(baseline.zinssatz * 1000) / 10,          // 3.5
    tilgung: Math.round(baseline.tilgungssatz * 1000) / 10,       // 2.0
    tilgungActive: baseline.tilgungActive ?? false,               // Toggle: Tilgung in Cashflow
    // Kaufnebenkosten — Listing-Override hat Vorrang (in %-Anzeige)
    agentCosts: baseline.maklerAnteil != null
      ? Math.round(baseline.maklerAnteil * 10000) / 100   // 0.0357 → 3.57
      : 3.57,
    notaryCosts: baseline.notarAnteil != null
      ? Math.round(baseline.notarAnteil * 10000) / 100    // 0.02 → 2.0
      : 2.0,
    grunderwerbSteuer: baseline.grunderwerbSteuerOverride ?? null, // null = auto
    // BaseRent-Override: null = Marktmiete, Zahl = manueller Wert
    baseRent: baseline.baseRentOverride ?? null,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET': return { ...state, [action.field]: action.value };
    case 'INIT': return action.state;
    default: return state;
  }
}

export function useSimulation(baseline, subscores, market) {
  const [params, dispatch] = useReducer(reducer, null);

  // Re-initialize when baseline becomes available
  useEffect(() => {
    if (baseline && !params) {
      dispatch({ type: 'INIT', state: buildInitial(baseline) });
    }
  }, [baseline, params]);

  const setParam = (field, value) => dispatch({ type: 'SET', field, value });
  const reset = () => { if (baseline) dispatch({ type: 'INIT', state: buildInitial(baseline) }); };

  const initial = baseline ? buildInitial(baseline) : null;
  const isActive = params && initial && (
    params.kaufpreisAnpassung !== 0 ||
    params.mieteAnpassung !== 0 ||
    params.leerstand !== 0 ||
    params.eigenkapital !== initial.eigenkapital ||
    params.zinssatz !== initial.zinssatz ||
    params.tilgung !== initial.tilgung ||
    params.tilgungActive !== initial.tilgungActive ||
    params.agentCosts !== initial.agentCosts ||
    params.notaryCosts !== initial.notaryCosts ||
    params.grunderwerbSteuer !== initial.grunderwerbSteuer ||
    params.baseRent !== initial.baseRent
  );

  const results = useMemo(() => {
    // Berechnung möglich wenn Marktmiete ODER BaseRent-Override vorhanden
    const hasMiete = baseline?.monatskaltmiete > 0 || (params?.baseRent != null && params.baseRent > 0);
    if (!hasMiete || !params) return null;
    // Market avg price per m² for Deal Score calculation (from market data or baseline)
    const avgPricePerSqm = market?.avg ?? baseline.avgPricePerSqm ?? null;

    // Convert display % → decimal for calculations
    const ekPct = params.eigenkapital / 100;       // 20 → 0.20
    const zinsDez = params.zinssatz / 100;          // 3.5 → 0.035
    // tilgungActive: wenn false → Tilgung = 0 in Cashflow-Berechnung
    const tilgDez = params.tilgungActive ? (params.tilgung / 100) : 0;  // 2.0 → 0.02 oder 0

    const simKaufpreis = Math.round(baseline.kaufpreis * (1 + params.kaufpreisAnpassung / 100));
    // BaseRent-Override: wenn gesetzt → als Basismietewert verwenden statt Marktmiete
    const baseMiete = params.baseRent != null && params.baseRent > 0
      ? params.baseRent
      : baseline.monatskaltmiete;
    const simMiete = Math.round(baseMiete * (1 + params.mieteAnpassung / 100));
    // Leerstand: reduce effective monthly rent proportionally (leerstand = vacant months per year)
    const effektiveMiete = Math.round(simMiete * (1 - (params.leerstand || 0) / 12));

    // Kaufnebenkosten: ggf. mit Listing-Override-Werten
    const simAgentCosts = (params.agentCosts ?? 3.57) / 100;
    const simNotaryCosts = (params.notaryCosts ?? 2.0) / 100;
    const nk = calculateKaufnebenkosten(simKaufpreis, baseline.bundesland, simAgentCosts, simNotaryCosts, params.grunderwerbSteuer);
    const gesamtkosten = simKaufpreis + nk.gesamt;
    const eigenkapital = Math.round(gesamtkosten * ekPct);
    const darlehensbetrag = gesamtkosten - eigenkapital;

    const cf = calculateMonthlyCashflow({
      monatskaltmiete: effektiveMiete,
      darlehensbetrag,
      zinssatz: zinsDez,
      tilgungssatz: tilgDez,
      wohnflaeche: baseline.wohnflaeche,
    });

    const jahreskaltmiete = effektiveMiete * 12;
    const bruttoRendite = calcBruttoRendite(jahreskaltmiete, simKaufpreis);
    const ekRendite = calcEKRendite({
      // cashflowJahr = Cashflow vor Tilgung (Hauptwert)
      cashflowVorTilgungJahr: cf.cashflowJahr, eigenkapital,
    });
    const faktor = calcFaktor(simKaufpreis, jahreskaltmiete);
    const breakEvenZins = calcBreakEvenZins({
      monatskaltmiete: simMiete, darlehensbetrag, tilgungssatz: tilgDez,
      wohnflaeche: baseline.wohnflaeche,
    });

    // Simulated sub-scores: Deal + Income recalculated client-side.
    // Object + Location remain static from subscores (no backend re-calc available).
    const simDealScore = calcDealScore(simKaufpreis, baseline.wohnflaeche, avgPricePerSqm);
    const simBruttoRendite = gesamtkosten > 0 ? (effektiveMiete * 12 / gesamtkosten) * 100 : null;
    const simIncomeScore = calcIncomeScore(simBruttoRendite);
    const originalObjectScore = subscores?.object?.score ?? 0;
    const originalLocationScore = subscores?.location?.score ?? 0;
    const simulatedTotal = Math.min(100, Math.max(0,
      simDealScore + simIncomeScore + originalObjectScore + originalLocationScore
    ));

    // DSCR: Net Operating Income / Kapitaldienst (Zins + Tilgung)
    // nettomiet = effektive Jahreskaltmiete minus Bewirtschaftungskosten
    const nettomiet = (effektiveMiete - (cf.bewirtschaftung ?? 0)) * 12;
    const kapitaldienst = cf.kreditrate * 12;
    const dscr = kapitaldienst > 0 ? Math.round((nettomiet / kapitaldienst) * 100) / 100 : null;

    return {
      cf, nk, eigenkapital, darlehensbetrag, monatsrate: cf.kreditrate,
      bruttoRendite, ekRendite, faktor, breakEvenZins,
      simKaufpreis, simMiete, effektiveMiete,
      simDealScore, simIncomeScore, simulatedTotal,
      dscr, nettomiet, kapitaldienst,
    };
  }, [baseline, params, subscores, market]);

  const delta = useMemo(() => {
    if (!results || !baseline?.cf) return null;
    const baselineScore = baseline.score ?? 0;
    return {
      // cashflow = Cashflow vor Tilgung (Hauptwert)
      cashflow: results.cf.cashflow - baseline.cf.cashflow,
      ekRendite: (results.ekRendite || 0) - (baseline.ekRendite || 0),
      bruttoRendite: (results.bruttoRendite || 0) - (baseline.bruttoRendite || 0),
      faktor: (results.faktor || 0) - (baseline.faktor || 0),
      eigenkapital: results.eigenkapital - baseline.eigenkapital,
      darlehensbetrag: results.darlehensbetrag - baseline.darlehensbetrag,
      nkGesamt: results.nk.gesamt - baseline.nk.gesamt,
      score: results.simulatedTotal - baselineScore,
      dscr: results.dscr != null && baseline.dscr != null
        ? Math.round((results.dscr - baseline.dscr) * 100) / 100
        : null,
    };
  }, [results, baseline]);

  return { params, setParam, reset, isActive, results, delta, baseline };
}
